import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, MutableRefObject, PointerEvent as ReactPointerEvent } from 'react';

import { constrainCommentPosition } from './commentDragGeometry.ts';
import type { CommentBounds, CommentPosition } from './commentDragGeometry.ts';

// Movement below this distance is a click, not a drag.
const DRAG_THRESHOLD_PX = 3;

type Placement = {
	// CSS left/top, relative to the fixed containing block.
	left: number;
	top: number;
	width: number;
	contentMaxHeight: number;
};

// A dropped box keeps its page position. `top` is in page coordinates: the
// viewport top plus the scroll offset of `scrollElements` when it was dropped.
type Anchor = {
	left: number;
	top: number;
	width: number;
	scrollElements: HTMLElement[];
};

type Drag = {
	pointerId: number;
	pointer: CommentPosition;
	start: CommentPosition;
	width: number;
	scrollElements: HTMLElement[];
	position?: CommentPosition;
};

type State = {
	resetKey: unknown;
	anchor?: Anchor;
	placement?: Placement;
};

export type CommentDragHandleProps = {
	onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
	onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
	onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
	onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
	onLostPointerCapture: (event: ReactPointerEvent<HTMLElement>) => void;
};

type Params = {
	sidebarRef: MutableRefObject<HTMLElement | null>;
	contentRef: MutableRefObject<HTMLElement | null>;
	annotationElement?: HTMLElement | null;
	/** Lowest viewport top the box can occupy, for example below the sticky page header. */
	minTop: number;
	/** Placement resets when this changes, for example when another comment opens. */
	resetKey: unknown;
	onDragged: (delta: { deltaX: number; deltaY: number }) => void;
};

// Track page scrolling, not the comment's own internal scroll. The annotation
// can live in a different scroll tree from the sidebar (for example the editor).
const getScrollElements = (element: HTMLElement): HTMLElement[] => {
	const elements: HTMLElement[] = [];
	for (let parent = element.parentElement; parent; parent = parent.parentElement) {
		if (parent.tagName !== 'BODY' && parent.tagName !== 'HTML') {
			const overflow = window.getComputedStyle(parent).overflowY;
			if (/auto|scroll|overlay/.test(overflow) && parent.scrollHeight > parent.clientHeight) {
				elements.push(parent);
			}
		}
	}
	return elements;
};

const getScrollOffset = (scrollElements: HTMLElement[]) =>
	window.scrollY + scrollElements.reduce((sum, el) => sum + el.scrollTop, 0);

// position: fixed is relative to the viewport unless an ancestor creates a
// containing block (transform, filter, contain, and so on).
const getFixedOrigin = (element: HTMLElement): CommentPosition => {
	for (let parent = element.parentElement; parent; parent = parent.parentElement) {
		const style = window.getComputedStyle(parent);
		if (
			(style.transform && style.transform !== 'none') ||
			(style.perspective && style.perspective !== 'none') ||
			(style.filter && style.filter !== 'none') ||
			/paint|layout|strict|content/.test(style.contain ?? '') ||
			/transform|perspective|filter/.test(style.willChange ?? '')
		) {
			const rect = parent.getBoundingClientRect();
			return { left: rect.left + parent.clientLeft, top: rect.top + parent.clientTop };
		}
	}
	return { left: 0, top: 0 };
};

const getBounds = (scrollElements: HTMLElement[], minTop: number): CommentBounds => {
	const rect = scrollElements[0]?.getBoundingClientRect();
	return {
		left: Math.max(0, rect?.left ?? 0),
		right: Math.min(window.innerWidth, rect?.right ?? window.innerWidth),
		top: Math.min(window.innerHeight, Math.max(minTop, rect?.top ?? 0)),
		bottom: Math.min(window.innerHeight, rect?.bottom ?? window.innerHeight),
	};
};

// Measure the box and work out where it can sit. Returns the on-screen
// position (for anchoring and analytics) and the CSS placement to render.
const measurePlacement = (
	sidebar: HTMLElement,
	content: HTMLElement,
	requested: CommentPosition,
	requestedWidth: number,
	scrollElements: HTMLElement[],
	minTop: number,
): { position: CommentPosition; placement: Placement } => {
	const bounds = getBounds(scrollElements, minTop);
	const width = Math.min(requestedWidth, Math.max(0, bounds.right - bounds.left));
	const headerHeight =
		sidebar.getBoundingClientRect().height - content.getBoundingClientRect().height;
	const contentMaxHeight = Math.max(0, bounds.bottom - bounds.top - headerHeight);
	const height = headerHeight + Math.min(content.scrollHeight, contentMaxHeight);
	const position = constrainCommentPosition(requested, width, height, bounds);
	const origin = getFixedOrigin(sidebar);
	return {
		position,
		placement: {
			left: position.left - origin.left,
			top: position.top - origin.top,
			width,
			contentMaxHeight,
		},
	};
};

const isSamePlacement = (a?: Placement, b?: Placement) =>
	a === b ||
	(!!a &&
		!!b &&
		a.left === b.left &&
		a.top === b.top &&
		a.width === b.width &&
		a.contentMaxHeight === b.contentMaxHeight);

/**
 * Lets the user drag the floating comment box. CommentSidebar owns the box, so
 * it applies the returned styles; the drag handle only spreads `handleProps`.
 */
export const useCommentDrag = ({
	sidebarRef,
	contentRef,
	annotationElement,
	minTop,
	resetKey,
	onDragged,
}: Params): {
	handleProps: CommentDragHandleProps;
	sidebarStyle?: CSSProperties;
	contentMaxHeight?: number;
} => {
	const [state, setState] = useState<State>({ resetKey });
	const dragRef = useRef<Drag>();

	// Opening another comment resets the placement.
	// https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
	if (state.resetKey !== resetKey) {
		setState({ resetKey });
	}

	const { anchor } = state;

	// Once dropped, keep the box at its page position as the page scrolls or resizes.
	useEffect(() => {
		const sidebar = sidebarRef.current;
		const content = contentRef.current;
		if (!anchor || !sidebar || !content) return;
		const sync = () => {
			if (dragRef.current) return;
			const { placement } = measurePlacement(
				sidebar,
				content,
				{ left: anchor.left, top: anchor.top - getScrollOffset(anchor.scrollElements) },
				anchor.width,
				anchor.scrollElements,
				minTop,
			);
			setState((current) =>
				current.anchor === anchor && !isSamePlacement(current.placement, placement)
					? { ...current, placement }
					: current,
			);
		};
		const scrollTargets: Array<HTMLElement | Window> = [window, ...anchor.scrollElements];
		scrollTargets.forEach((target) => target.addEventListener('scroll', sync, { passive: true }));
		window.addEventListener('resize', sync);
		// The box changes height as replies are typed or loaded.
		const observer = new ResizeObserver(sync);
		observer.observe(content);
		return () => {
			scrollTargets.forEach((target) => target.removeEventListener('scroll', sync));
			window.removeEventListener('resize', sync);
			observer.disconnect();
		};
	}, [anchor, sidebarRef, contentRef, minTop]);

	const endDrag = (event: ReactPointerEvent<HTMLElement>, completed: boolean) => {
		const drag = dragRef.current;
		if (drag?.pointerId !== event.pointerId) return;
		dragRef.current = undefined;
		const { position } = drag;
		if (!position) return;
		// A cancelled drag still leaves the box where it was last shown.
		setState((current) => ({
			...current,
			anchor: {
				left: position.left,
				top: position.top + getScrollOffset(drag.scrollElements),
				width: drag.width,
				scrollElements: drag.scrollElements,
			},
		}));
		if (completed) {
			onDragged({ deltaX: position.left - drag.start.left, deltaY: position.top - drag.start.top });
		}
	};

	const handleProps: CommentDragHandleProps = {
		onPointerDown: (event) => {
			const sidebar = sidebarRef.current;
			if (!event.isPrimary || event.button !== 0 || !sidebar) return;
			// Capture keeps move/up events on the handle, including over iframes
			// and outside the window, so no document listeners are needed.
			event.currentTarget.setPointerCapture(event.pointerId);
			const rect = sidebar.getBoundingClientRect();
			dragRef.current = {
				pointerId: event.pointerId,
				pointer: { left: event.clientX, top: event.clientY },
				start: { left: rect.left, top: rect.top },
				width: anchor?.width ?? rect.width,
				// Found at drag time, so containers that overflowed after the box opened count.
				scrollElements: getScrollElements(annotationElement ?? sidebar),
			};
		},
		onPointerMove: (event) => {
			const drag = dragRef.current;
			const sidebar = sidebarRef.current;
			const content = contentRef.current;
			if (drag?.pointerId !== event.pointerId || !sidebar || !content) return;
			const dx = event.clientX - drag.pointer.left;
			const dy = event.clientY - drag.pointer.top;
			if (!drag.position && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
			// Browsers align pointermove with animation frames, so this runs at most once a frame.
			const { position, placement } = measurePlacement(
				sidebar,
				content,
				{ left: drag.start.left + dx, top: drag.start.top + dy },
				drag.width,
				drag.scrollElements,
				minTop,
			);
			drag.position = position;
			setState((current) => ({ ...current, placement }));
		},
		onPointerUp: (event) => endDrag(event, true),
		onPointerCancel: (event) => endDrag(event, false),
		onLostPointerCapture: (event) => endDrag(event, false),
	};

	const { placement } = state;
	return {
		handleProps,
		sidebarStyle: placement && {
			position: 'fixed',
			left: placement.left,
			top: placement.top,
			right: 'auto',
			width: placement.width,
		},
		contentMaxHeight: placement?.contentMaxHeight,
	};
};
